import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ContentSection = styled.section<{
  themeMode: 'default' | 'compact';
}>`
  display: flex;
  gap: 2rem;
`;

export const QuestionAssistantContainer = styled.div<{ $isCompact?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: ${({ $isCompact }) => ($isCompact ? 'stretch' : 'center')};
  flex-direction: column;
  gap: ${({ $isCompact }) => ($isCompact ? '1rem' : '0')};
`;

/** Shown under compact Seek Assistance while a suggested action opens Ask (create session → invoke → navigate). */
export const SuggestedActionProgressStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.88rem;
  font-weight: 500;
`;

export const ContentSectionContainer = styled.div<{ $isCompact?: boolean }>`
  max-width: ${({ $isCompact }) => ($isCompact ? '1180px' : 'none')};
  padding: ${({ $isCompact }) =>
    $isCompact ? '2rem 2rem 2.5rem' : '2rem 2rem'};
  margin: 0 auto;
`;

export const AssistantFeatureCard = styled.section`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background:
    radial-gradient(
      circle at top right,
      ${({ theme }) => `${theme.colors.primary}14`} 0%,
      transparent 48%
    ),
    ${({ theme }) => theme.colors.background};
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  padding: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const AssistantFeatureHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const AssistantFeatureEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: fit-content;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #155e75;
  background: #ecfeff;
  border: 1px solid #a5f3fc;
`;

export const AssistantFeatureTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const AssistantFeatureSubtitle = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const AssistantFeatureActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

export const GuidePanel = styled.section`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const GuideHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

export const GuideTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const GuideSubtitle = styled.p`
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const GuideStepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const GuideStepCard = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

export const GuideStepIcon = styled.div`
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}14`};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}33`};
`;

export const GuideStepTitle = styled.h4`
  margin: 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const GuideStepText = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 1;
`;

export const LandingHero = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.75rem;
`;

export const LandingGreeting = styled.div`
  width: 100%;
  font-size: 1.35rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const LandingSubheading = styled.div`
  width: 100%;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: -0.25rem;
`;

export const HeroIntentRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

export const HeroIntentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.8rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: translateY(1px);
  }
`;

export const WorkspaceSection = styled.section<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isCompact }) => ($isCompact ? '1.1rem' : '1rem')};
  margin-top: ${({ $isCompact }) => ($isCompact ? '0.6rem' : '0.75rem')};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: ${({ theme, $isCompact }) =>
    $isCompact ? `1px solid ${theme.colors.border.light}` : 'none'};
  background: ${({ theme, $isCompact }) =>
    $isCompact ? theme.colors.background : 'transparent'};
  padding: ${({ $isCompact }) => ($isCompact ? '1rem' : '0')};
`;

export const CreateWorkspaceCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 1rem 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const CreateWorkspaceCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const CreateWorkspaceCardTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const CreateWorkspaceCardSubtitle = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ResultOptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ResultOptionsHeader = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 0.25rem;
`;

/** List of clickable result option cards – stacked vertically for clarity */
export const ResultOptionsChipList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
`;

/** Single option card – clearly clickable, scannable, and welcoming */
export const ResultOptionChip = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease,
    transform 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    border-left-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    background-color: ${({ theme }) => theme.colors.primary}08;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.995);
  }

  &[data-disabled='true'] {
    cursor: default;
    transform: none;
  }

  &[data-disabled='true']:hover {
    border-color: ${({ theme }) => theme.colors.border.light};
    border-left-color: ${({ theme }) => theme.colors.primary};
    box-shadow: none;
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

/** Action and Feature on the same row – acts as card “header” */
export const ResultOptionActionFeatureRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`;

type PillVariant =
  | 'ask-open'
  | 'ask-create'
  | 'action-open'
  | 'action-create'
  | 'feature-ask'
  | 'feature-collection'
  | 'feature-document'
  | 'feature-persona'
  | 'feature-prompt'
  | 'feature-workflow'
  | 'feature-workspace'
  | 'workspace-private'
  | 'workspace-standard'
  | 'workspace-collaborative';

export const ResultOptionPill = styled.span<{ $variant: PillVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  line-height: 1.2;
  background-color: ${({ $variant }) =>
    $variant === 'ask-open'
      ? '#dbeafe'
      : $variant === 'ask-create'
        ? '#d1fae5'
        : $variant === 'action-open'
          ? '#f1f5f9'
          : $variant === 'action-create'
            ? '#f1f5f9'
            : $variant === 'feature-ask'
              ? '#e0e7ff'
              : $variant === 'feature-collection'
                ? '#ffedd5'
                : $variant === 'feature-document'
                  ? '#dbeafe'
                  : $variant === 'feature-persona'
                    ? '#ede9fe'
                    : $variant === 'feature-prompt'
                      ? '#e0e7ff'
                      : $variant === 'feature-workflow'
                        ? '#cffafe'
                        : $variant === 'feature-workspace'
                          ? '#f1f5f9'
                          : $variant === 'workspace-private'
                            ? '#f8fafc'
                            : $variant === 'workspace-standard'
                              ? '#eff6ff'
                              : $variant === 'workspace-collaborative'
                                ? '#ecfdf5'
                                : '#f1f5f9'};
  color: ${({ $variant }) =>
    $variant === 'ask-open'
      ? '#1d4ed8'
      : $variant === 'ask-create'
        ? '#047857'
        : $variant === 'action-open'
          ? '#475569'
          : $variant === 'action-create'
            ? '#475569'
            : $variant === 'feature-ask'
              ? '#4338ca'
              : $variant === 'feature-collection'
                ? '#c2410c'
                : $variant === 'feature-document'
                  ? '#1d4ed8'
                  : $variant === 'feature-persona'
                    ? '#5b21b6'
                    : $variant === 'feature-prompt'
                      ? '#3730a3'
                      : $variant === 'feature-workflow'
                        ? '#0e7490'
                        : $variant === 'feature-workspace'
                          ? '#475569'
                          : $variant === 'workspace-private'
                            ? '#64748b'
                            : $variant === 'workspace-standard'
                              ? '#1d4ed8'
                              : $variant === 'workspace-collaborative'
                                ? '#047857'
                                : '#475569'};
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'ask-open'
        ? '#93c5fd'
        : $variant === 'ask-create'
          ? '#6ee7b7'
          : $variant === 'action-open'
            ? '#cbd5e1'
            : $variant === 'action-create'
              ? '#cbd5e1'
              : $variant === 'feature-ask'
                ? '#a5b4fc'
                : $variant === 'feature-collection'
                  ? '#fdba74'
                  : $variant === 'feature-document'
                    ? '#93c5fd'
                    : $variant === 'feature-persona'
                      ? '#c4b5fd'
                      : $variant === 'feature-prompt'
                        ? '#a5b4fc'
                        : $variant === 'feature-workflow'
                          ? '#67e8f9'
                          : $variant === 'feature-workspace'
                            ? '#cbd5e1'
                            : $variant === 'workspace-private'
                              ? '#e2e8f0'
                              : $variant === 'workspace-standard'
                                ? '#93c5fd'
                                : $variant === 'workspace-collaborative'
                                  ? '#6ee7b7'
                                  : '#e2e8f0'};
`;

export const ResultOptionPillIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

export const ResultOptionWorkspaceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

export const ResultOptionField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

export const ResultOptionFieldLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ResultOptionFieldValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ResultOptionChipMeta = styled.span`
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Button to open the useful prompt in a new Chat session (stops propagation so chip click is not triggered) */
export const ResultOptionOpenInChatButton = styled.button`
  margin-top: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #047857;
  background-color: #d1fae5;
  border: 1px solid #6ee7b7;
  border-radius: ${({ theme }) => theme.borderRadius.xs};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background-color: #a7f3d0;
    border-color: #34d399;
  }

  &:focus-visible {
    outline: 2px solid #047857;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

/* Legacy card layout – kept for reference or alternate layouts */
export const ResultOptionCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.9rem 1rem;
  background-color: ${({ theme }) => theme.colors.background};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

export const ResultOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ResultOptionTitle = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ResultOptionMeta = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const WorkspacePickerContainer = styled.div`
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  background-color: ${({ theme }) => theme.colors.primary}08;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const WorkspacePickerTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const WorkspacePickerSubtitle = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const WorkspacePickerList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/** Wrapper for the History accordion block (matches QuestionAssistant history layout) */
export const HistoryBlock = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

/** Truncated question text as history item title */
export const HistoryItemTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

/** Answer or timestamp text inside a history item */
export const HistoryItemAnswer = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.45;
`;

export const SessionTemplateModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

export const SessionTemplateModalCard = styled.div`
  width: min(640px, 100%);
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.2);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const SessionTemplateModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SessionTemplateModalText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SessionTemplateModalActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const SessionTemplateButton = styled.button<{
  $variant?: 'primary' | 'secondary';
}>`
  min-height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'primary'
        ? theme.colors.primary
        : theme.colors.border.light};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.background};
  color: ${({ theme, $variant }) =>
    $variant === 'primary' ? '#ffffff' : theme.colors.text.primary};
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.15s ease;

  &:hover {
    opacity: 0.94;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;
